// import React, { useState } from "react";
// import "./Select.css";

// type SelectProps = {
//   value?: string;
//   defaultValue?: string;
//   onValueChange?: (val: string) => void;
//   children: React.ReactNode;
//   className?: string;
// };

// const SelectContext = React.createContext<{
//   value: string | undefined;
//   setValue: (val: string) => void;
//   open: boolean;
//   setOpen: (o: boolean) => void;
// } | null>(null);

// const Select = ({ value: valueProp, defaultValue, onValueChange, children, className }: SelectProps) => {
//   const [value, setValueState] = useState(defaultValue);
//   const [open, setOpen] = useState(false);

//   const setValue = (val: string) => {
//     setValueState(val);
//     onValueChange?.(val);
//     setOpen(false);
//   };

//   return (
//     <SelectContext.Provider value={{ value: valueProp ?? value, setValue, open, setOpen }}>
//       <div className={`select-root ${className || ""}`}>{children}</div>
//     </SelectContext.Provider>
//   );
// };

// const SelectTrigger = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
//   ({ className, children, ...props }, ref) => {
//     const ctx = React.useContext(SelectContext);
//     if (!ctx) throw new Error("SelectTrigger must be used inside <Select>");
//     return (
//       <button
//         ref={ref}
//         className={`select-trigger ${className || ""}`}
//         onClick={() => ctx.setOpen(!ctx.open)}
//         {...props}
//       >
//         <span className="select-value">{children}</span>
//         <span className="select-icon">▼</span>
//       </button>
//     );
//   }
// );

// const SelectValue: React.FC<{ placeholder?: string }> = ({ placeholder }) => {
//   const ctx = React.useContext(SelectContext);
//   if (!ctx) throw new Error("SelectValue must be used inside <Select>");
//   return <span>{ctx.value || placeholder}</span>;
// };

// const SelectContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
//   ({ className, children, ...props }, ref) => {
//     const ctx = React.useContext(SelectContext);
//     if (!ctx) throw new Error("SelectContent must be used inside <Select>");
//     if (!ctx.open) return null;

//     return (
//       <div ref={ref} className={`select-content ${className || ""}`} {...props}>
//         {children}
//       </div>
//     );
//   }
// );

// type SelectItemProps = React.HTMLAttributes<HTMLDivElement> & { value: string };

// const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
//   ({ className, children, value, ...props }, ref) => {
//     const ctx = React.useContext(SelectContext);
//     if (!ctx) throw new Error("SelectItem must be used inside <Select>");
//     const selected = ctx.value === value;

//     return (
//       <div
//         ref={ref}
//         className={`select-item ${selected ? "selected" : ""} ${className || ""}`}
//         onClick={() => ctx.setValue(value)}
//         {...props}
//       >
//         {selected && <span className="select-check">✔</span>}
//         {children}
//       </div>
//     );
//   }
// );

// const SelectLabel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
//   ({ className, ...props }, ref) => (
//     <div ref={ref} className={`select-label ${className || ""}`} {...props} />
//   )
// );

// const SelectSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
//   ({ className, ...props }, ref) => (
//     <div ref={ref} className={`select-separator ${className || ""}`} {...props} />
//   )
// );

// export { Select, SelectTrigger, SelectContent, SelectItem, SelectLabel, SelectSeparator, SelectValue };
import React, { useState, useRef, useEffect } from "react";
import "./Select.css";

type SelectProps = {
  value?: string;
  defaultValue?: string;
  onValueChange?: (val: string | any) => void;
  children: React.ReactNode;
  className?: string;
};

const SelectContext = React.createContext<{
  value: string | undefined;
  setValue: (val: string) => void;
  open: boolean;
  setOpen: (o: boolean) => void;
} | null>(null);

const Select = ({ value: valueProp, defaultValue, onValueChange, children, className }: SelectProps) => {
  const [value, setValueState] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const setValue = (val: string) => {
    setValueState(val);
    onValueChange?.(val);
    setOpen(false);
  };

  // ✅ close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  return (
    <SelectContext.Provider value={{ value: valueProp ?? value, setValue, open, setOpen }}>
      <div ref={rootRef} className={`select-root ${className || ""}`}>
        {children}
      </div>
    </SelectContext.Provider>
  );
};

const SelectTrigger = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, children, ...props }, ref) => {
    const ctx = React.useContext(SelectContext);
    if (!ctx) throw new Error("SelectTrigger must be used inside <Select>");
    return (
      <button
      type="button"
        ref={ref}
        className={`select-trigger ${className || ""}`}
        onClick={() => ctx.setOpen(!ctx.open)}
        {...props}
      >
        <span className="select-value">{children}</span>
        <span className="select-icon">▼</span>
      </button>
    );
  }
);

const SelectValue: React.FC<{ placeholder?: string }> = ({ placeholder }) => {
  const ctx = React.useContext(SelectContext);
  if (!ctx) throw new Error("SelectValue must be used inside <Select>");
  return <span>{ctx.value || placeholder}</span>;
};

const SelectContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    const ctx = React.useContext(SelectContext);
    if (!ctx) throw new Error("SelectContent must be used inside <Select>");
    if (!ctx.open) return null;

    return (
      <div ref={ref} className={`select-content ${className || ""}`} {...props}>
        {children}
      </div>
    );
  }
);

type SelectItemProps = React.HTMLAttributes<HTMLDivElement> & { value: string };

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  ({ className, children, value, ...props }, ref) => {
    const ctx = React.useContext(SelectContext);
    if (!ctx) throw new Error("SelectItem must be used inside <Select>");
    const selected = ctx.value === value;

    return (
      <div
        ref={ref}
        className={`select-item ${selected ? "selected" : ""} ${className || ""}`}
        onClick={() => ctx.setValue(value)}
        {...props}
      >
        {selected && <span className="select-check">✔</span>}
        {children}
      </div>
    );
  }
);

const SelectLabel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={`select-label ${className || ""}`} {...props} />
  )
);

const SelectSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={`select-separator ${className || ""}`} {...props} />
  )
);

export { Select, SelectTrigger, SelectContent, SelectItem, SelectLabel, SelectSeparator, SelectValue };
